import {
  SafetyCheckSnapshot,
  SafetyCheckValidationErrors,
  SafetyCheckValidationField,
  emptySafetyCheckSnapshot
} from "@js/worker/safetyCheckController";
import { appContainer } from "@js/container";
import { AlpineMagics } from "@js/shared/alpine";
import type { AppLayoutStore } from "@js/worker/appLayoutStore";

export const EVALUATION_REVIEW_CLOSED_KEY = 'evaluationReviewClosed';
export const EVALUATION_REVIEW_HISTORY_DEPTH_KEY = 'evaluationReviewHistoryDepth';
export const EVALUATION_REVIEW_CLOSING_KEY = 'evaluationReviewClosing';

const EVALUATION_REVIEW_BOTTOM_NAVIGATION_HIDDEN_REASON = 'evaluation-review';
const LEAVE_CONFIRMATION_BOTTOM_NAVIGATION_HIDDEN_REASON = 'review-leave-confirmation';

export const enableEvaluationReview = () => {
  appContainer.setState(EVALUATION_REVIEW_CLOSED_KEY, false);
};

export const evaluationReviewPath = (): string | null => {
  if (appContainer.getState(EVALUATION_REVIEW_CLOSED_KEY) !== false) {
    return null;
  }

  return document.querySelector<HTMLElement>('[data-evaluation-review-path]')?.dataset.evaluationReviewPath || null;
};

export const initializeEvaluationReview = (): string | null => {
  if (appContainer.getState(EVALUATION_REVIEW_CLOSING_KEY) === true) {
    appContainer.setState(EVALUATION_REVIEW_CLOSING_KEY, false);
    return null;
  }

  enableEvaluationReview();
  return evaluationReviewPath();
};

type ReviewAnswer = {
  score: number | null;
  options: string[];
  answerText: string;
};

type ReviewHistoryTarget = 'bulk' | 'offers';
type TurboSubmitEndEvent = CustomEvent<{ success: boolean }>;

type ReviewHistoryState = Record<string, unknown> & {
  evaluationReview?: boolean;
  evaluationReviewGuard?: boolean;
  evaluationReviewLeaveConfirmation?: boolean;
  evaluationReviewDepth?: number;
  evaluationReviewHasPreviousHistory?: boolean;
  step?: number;
};

const historyStateWithoutReview = (): ReviewHistoryState => {
  const state = { ...(history.state as ReviewHistoryState | null) };
  delete state.turbo;
  delete state.evaluationReview;
  delete state.evaluationReviewGuard;
  delete state.evaluationReviewLeaveConfirmation;
  delete state.evaluationReviewDepth;
  delete state.evaluationReviewHasPreviousHistory;
  delete state.step;
  return state;
};

const isReviewHistoryState = (state: ReviewHistoryState | null): boolean =>
  state?.evaluationReview === true ||
  state?.evaluationReviewGuard === true ||
  state?.evaluationReviewLeaveConfirmation === true;

export default {
  step: 0,
  reviewQuestionCount: 0,
  requiresSafetyCheck: false,
  safetyCheck: emptySafetyCheckSnapshot(),
  safetyCheckErrors: {} as SafetyCheckValidationErrors,
  reviewAnswers: {} as Record<string, ReviewAnswer>,
  returnToConfirmation: false,
  showLeaveConfirmation: false,
  safetyImageUploadFailureSource: null as 'add' | 'replace' | null,
  submitting: false,
  automatic: false,
  reviewStartStep: 0,
  bulkNewUrl: '',
  hasHistoryBeforeReview: false,
  pendingHistoryTarget: null as ReviewHistoryTarget | null,
  leaveConfirmationStep: 0,
  leaveConfirmationDepth: 2,

  getAlpineMagic(): AlpineMagics<unknown> {
    return this as unknown as AlpineMagics<unknown>;
  },

  setupReviewFlow(questionCount: number, requiresSafetyCheck: boolean, initialAnswers: Record<string, ReviewAnswer>, initialStep = 0, automatic = false, bulkNewUrl = '', reviewStartStep = initialStep) {
    this.step = initialStep;
    this.reviewStartStep = reviewStartStep;
    this.reviewQuestionCount = questionCount;
    this.requiresSafetyCheck = requiresSafetyCheck;
    this.reviewAnswers = initialAnswers;
    this.safetyCheckErrors = {};
    this.safetyImageUploadFailureSource = null;
    this.submitting = false;
    this.pendingHistoryTarget = null;
    this.automatic = automatic;
    this.bulkNewUrl = bulkNewUrl;
    this.hideEvaluationReviewBottomNavigation();
    this.installReviewHistory();
  },

  onSafetyCheckChanged(snapshot: SafetyCheckSnapshot) {
    this.safetyCheck = snapshot;
    this.safetyCheckErrors = Object.fromEntries(
      Object.entries(this.safetyCheckErrors).filter(([field]) => snapshot.validationErrors[field as SafetyCheckValidationField])
    ) as SafetyCheckValidationErrors;
  },

  safetyCheckError(field: SafetyCheckValidationField): string {
    return this.safetyCheckErrors[field] || '';
  },

  async validateSafetyCheckAndContinue() {
    this.safetyCheckErrors = { ...this.safetyCheck.validationErrors };
    const firstInvalidField = Object.keys(this.safetyCheckErrors)[0] as SafetyCheckValidationField | undefined;
    if (!firstInvalidField) {
      this.safetyCheck.reportDetail = this.safetyCheck.reportDetail.trim();
      this.nextFromSafetyCheck();
      return;
    }

    await this.getAlpineMagic().$nextTick();
    this.getAlpineMagic().$refs.workerReviewForm
      .querySelector<HTMLElement>(`[data-safety-check-field="${firstInvalidField}"]`)
      ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  },

  clickStar(starNum: number, questionId: string) {
    this.reviewAnswers[questionId].score = starNum;
  },

  selectItem(options: string[], option: string) {
    if (options.includes(option)) {
      options.splice(options.indexOf(option), 1);
    } else {
      options.push(option);
    }
  },

  nextFromReview() {
    if (this.step < this.reviewQuestionCount - 1) {
      this.goToStep(this.step + 1);
      return;
    }

    const nextStep = this.returnToConfirmation ? this.confirmationStep() :
      (this.requiresSafetyCheck ? this.reviewQuestionCount : this.confirmationStep());
    this.returnToConfirmation = false;
    this.goToStep(nextStep);
  },

  nextFromSafetyCheck() {
    this.returnToConfirmation = false;
    this.goToStep(this.confirmationStep());
  },

  reviewAnswerComplete(questionId: string, questionType: string, optional: boolean): boolean {
    const answer = this.reviewAnswers[questionId];
    if (questionType === 'text') {
      return optional || answer.answerText.trim() !== '';
    }
    if (questionType === 'select' || questionType === 'multi_select') {
      return answer.options.length > 0 && answer.options[0] !== '';
    }
    if (questionType === 'score_with_multi_select') {
      return answer.score != null && (answer.score <= 3 || answer.options.length > 0);
    }
    return answer.score != null;
  },

  confirmationStep(): number {
    return this.reviewQuestionCount + (this.requiresSafetyCheck ? 1 : 0);
  },

  isConfirmationStep(): boolean {
    return this.step === this.confirmationStep();
  },

  confirmationHasNoReports(): boolean {
    return this.requiresSafetyCheck &&
      this.safetyCheck.reportStatus === 'none' &&
      this.safetyCheck.externalContactStatus === 'none';
  },

  reviewModalTitle(): string {
    if (this.step < this.reviewQuestionCount) {
      return '業務先の評価をしてください';
    }
    if (this.requiresSafetyCheck && this.step === this.reviewQuestionCount) {
      return '安心してお仕事いただくための確認';
    }
    return '入力内容の確認';
  },

  openSafetyImageUploadFailure(source: 'add' | 'replace') {
    this.safetyImageUploadFailureSource = source;
    this.getAlpineMagic().$nextTick(() => {
      const dialog = this.getAlpineMagic().$refs.safetyImageUploadFailureModal as HTMLDialogElement;
      if (!dialog.open) {
        dialog.showModal();
      }
      requestAnimationFrame(() => dialog.focus({ preventScroll: true }));
    });
  },

  closeSafetyImageUploadFailure() {
    const dialog = this.getAlpineMagic().$refs.safetyImageUploadFailureModal as HTMLDialogElement;
    if (dialog.open) {
      dialog.close();
    }
    this.safetyImageUploadFailureSource = null;
  },

  reviewAnswerText(questionId: string): string {
    const answer = this.reviewAnswers[questionId];
    if (answer.answerText !== '') {
      return answer.answerText;
    }
    if (answer.options.length > 0) {
      return answer.options.filter(Boolean).join('、');
    }
    return answer.score == null ? 'ー' : String(answer.score);
  },

  previousStep() {
    if (this.shouldReturnToBulkNew()) {
      this.goToBulkNew();
      return;
    }

    history.back();
  },

  shouldReturnToBulkNew(): boolean {
    return this.automatic && this.step === this.reviewStartStep;
  },

  goToBulkNew() {
    if (this.pendingHistoryTarget) {
      return;
    }

    this.showEvaluationReviewBottomNavigation();
    this.moveToReviewGuard('bulk');
  },

  editReviews() {
    if (this.automatic) {
      return;
    }

    this.returnToConfirmation = true;
    this.goToStep(0);
  },

  editSafetyCheck(section: 'report' | 'externalContact' = 'report') {
    this.returnToConfirmation = true;
    this.goToStep(this.reviewQuestionCount);

    if (section === 'externalContact') {
      this.getAlpineMagic().$nextTick(() => {
        requestAnimationFrame(() => {
          const root = this.getAlpineMagic().$refs.userReviewRoot;
          const target = root.querySelector<HTMLElement>('[data-safety-check-section="externalContact"]');
          target?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
      });
    }
  },

  reviewHistoryState(step: number, depth: number): ReviewHistoryState {
    return {
      ...historyStateWithoutReview(),
      evaluationReview: true,
      evaluationReviewDepth: depth,
      evaluationReviewHasPreviousHistory: this.hasHistoryBeforeReview,
      step
    };
  },

  goToStep(step: number) {
    this.step = step;
    const reviewHistoryDepth = Number(appContainer.getState(EVALUATION_REVIEW_HISTORY_DEPTH_KEY)) + 1;
    appContainer.setState(EVALUATION_REVIEW_HISTORY_DEPTH_KEY, reviewHistoryDepth);
    history.pushState(this.reviewHistoryState(step, reviewHistoryDepth), '', location.href);
    this.scrollReviewModalToTop();
  },

  installReviewHistory() {
    const currentState = history.state as ReviewHistoryState | null;
    if (currentState?.evaluationReview) {
      const reviewHistoryDepth = Number(currentState.evaluationReviewDepth);
      this.hasHistoryBeforeReview = currentState.evaluationReviewHasPreviousHistory === true;
      appContainer.setState(EVALUATION_REVIEW_HISTORY_DEPTH_KEY, reviewHistoryDepth);
      history.replaceState(this.reviewHistoryState(this.step, reviewHistoryDepth), '', location.href);
      return;
    }

    appContainer.setState(EVALUATION_REVIEW_HISTORY_DEPTH_KEY, 2);
    history.replaceState({ ...historyStateWithoutReview(), evaluationReviewGuard: true }, '', location.href);
    history.pushState(this.reviewHistoryState(this.step, 2), '', location.href);
    // pushState places the review at the history tail. Two entries mean that only the guard and review exist.
    this.hasHistoryBeforeReview = history.length > 2;
    history.replaceState(this.reviewHistoryState(this.step, 2), '', location.href);
  },

  moveToReviewGuard(target: ReviewHistoryTarget) {
    this.pendingHistoryTarget = target;
    const reviewHistoryDepth = Number(appContainer.getState(EVALUATION_REVIEW_HISTORY_DEPTH_KEY));
    history.go(-(reviewHistoryDepth - 1));
  },

  replaceReviewGuard(target: ReviewHistoryTarget) {
    this.pendingHistoryTarget = null;
    const path = target === 'bulk' ? this.bulkNewUrl : '/offers';
    this.visitTopLevel(path);
  },

  visitTopLevel(path: string) {
    appContainer.turbo.visit(path, { action: 'replace' });
  },

  pushLeaveConfirmationHistory() {
    history.pushState({
      ...historyStateWithoutReview(),
      evaluationReviewLeaveConfirmation: true,
      evaluationReviewDepth: this.leaveConfirmationDepth,
      evaluationReviewHasPreviousHistory: this.hasHistoryBeforeReview,
      step: this.leaveConfirmationStep
    }, '', location.href);
  },

  openBrowserBackLeaveConfirmation() {
    this.leaveConfirmationStep = this.step;
    this.leaveConfirmationDepth = Number(appContainer.getState(EVALUATION_REVIEW_HISTORY_DEPTH_KEY));
    this.setLeaveConfirmationVisible(true);
    this.pushLeaveConfirmationHistory();
  },

  handleReviewPopstate(event: PopStateEvent) {
    const state = event.state as ReviewHistoryState | null;

    if (this.pendingHistoryTarget) {
      event.stopImmediatePropagation();
      if (state?.evaluationReviewGuard) {
        this.replaceReviewGuard(this.pendingHistoryTarget);
      }
      return;
    }

    if (appContainer.getState(EVALUATION_REVIEW_CLOSING_KEY) === true) {
      if (isReviewHistoryState(state)) {
        event.stopImmediatePropagation();
        this.visitTopLevel('/offers');
      }
      return;
    }

    if (this.showLeaveConfirmation) {
      event.stopImmediatePropagation();
      this.pushLeaveConfirmationHistory();
      return;
    }

    event.stopImmediatePropagation();
    if (state?.evaluationReview) {
      this.step = Number(state.step);
      appContainer.setState(EVALUATION_REVIEW_HISTORY_DEPTH_KEY, Number(state.evaluationReviewDepth));
      this.scrollReviewModalToTop();
      return;
    }
    if (this.automatic) {
      this.showEvaluationReviewBottomNavigation();
      this.replaceReviewGuard('bulk');
      return;
    }
    if (!this.hasInput()) {
      this.confirmReviewClose(true);
      return;
    }
    this.openBrowserBackLeaveConfirmation();
  },

  scrollReviewModalToTop() {
    document.querySelector<HTMLElement>('#modal-box-modal-box-user-review-modal [data-scrollable]')?.scrollTo(0, 0);
  },

  reviewCreate() {
    if (this.submitting) {
      return;
    }

    const form = this.getAlpineMagic().$refs.workerReviewForm as HTMLFormElement | undefined;
    if (!form) {
      return;
    }

    this.submitting = true;
    form.requestSubmit();
  },

  handleReviewSubmitEnd(event: TurboSubmitEndEvent) {
    if (event.detail.success) {
      this.showEvaluationReviewBottomNavigation();
      return;
    }

    this.submitting = false;
  },

  appLayoutStore(): AppLayoutStore | undefined {
    const stores = this.getAlpineMagic().$store as { appLayout?: AppLayoutStore } | undefined;
    return stores?.appLayout;
  },

  hideEvaluationReviewBottomNavigation() {
    this.appLayoutStore()?.hideBottomNavigation(EVALUATION_REVIEW_BOTTOM_NAVIGATION_HIDDEN_REASON);
  },

  showEvaluationReviewBottomNavigation() {
    this.appLayoutStore()?.showBottomNavigation(EVALUATION_REVIEW_BOTTOM_NAVIGATION_HIDDEN_REASON);
  },

  setLeaveConfirmationVisible(visible: boolean) {
    this.showLeaveConfirmation = visible;
    const appLayoutStore = this.appLayoutStore();
    if (visible) {
      appLayoutStore?.hideBottomNavigation(LEAVE_CONFIRMATION_BOTTOM_NAVIGATION_HIDDEN_REASON);
    } else {
      appLayoutStore?.showBottomNavigation(LEAVE_CONFIRMATION_BOTTOM_NAVIGATION_HIDDEN_REASON);
    }
  },

  hasInput(): boolean {
    const hasReviewInput = Object.values(this.reviewAnswers).some((answer) =>
      answer.score !== null || answer.options.filter(Boolean).length > 0 || answer.answerText.trim() !== ''
    );
    const safetyCheck = this.safetyCheck;
    const hasSafetyCheckInput = safetyCheck.reportStatus !== '' || safetyCheck.reportCategories.length > 0 ||
      safetyCheck.reportDetail.trim() !== '' || safetyCheck.externalContactStatus !== '' ||
      safetyCheck.externalContactCase !== '' || safetyCheck.images.length > 0;
    return hasReviewInput || hasSafetyCheckInput;
  },

  requestReviewClose() {
    if (!this.hasInput()) {
      this.confirmReviewClose();
      return;
    }
    this.leaveConfirmationStep = this.step;
    this.leaveConfirmationDepth = Number(appContainer.getState(EVALUATION_REVIEW_HISTORY_DEPTH_KEY));
    this.setLeaveConfirmationVisible(true);
  },

  dismissEvaluationReview() {
    appContainer.setState(EVALUATION_REVIEW_CLOSED_KEY, true);
  },

  closeBulkReview() {
    this.dismissEvaluationReview();
    appContainer.setState(EVALUATION_REVIEW_CLOSING_KEY, true);

    if (history.length > 1) {
      history.back();
      return;
    }

    this.visitTopLevel('/offers');
  },

  cancelReviewClose() {
    if ((history.state as ReviewHistoryState | null)?.evaluationReviewLeaveConfirmation) {
      this.step = this.leaveConfirmationStep;
      appContainer.setState(EVALUATION_REVIEW_HISTORY_DEPTH_KEY, this.leaveConfirmationDepth);
      history.replaceState(
        this.reviewHistoryState(this.leaveConfirmationStep, this.leaveConfirmationDepth),
        '',
        location.href
      );
      this.scrollReviewModalToTop();
    }
    this.setLeaveConfirmationVisible(false);
  },

  confirmReviewClose(fromReviewGuard = false) {
    if (this.pendingHistoryTarget) {
      return;
    }

    this.dismissEvaluationReview();
    appContainer.setState(EVALUATION_REVIEW_CLOSING_KEY, true);
    const reviewHistoryDepth = Number(appContainer.getState(EVALUATION_REVIEW_HISTORY_DEPTH_KEY));
    this.setLeaveConfirmationVisible(false);
    this.showEvaluationReviewBottomNavigation();

    if (this.hasHistoryBeforeReview) {
      history.go(fromReviewGuard ? -(reviewHistoryDepth - 1) : -reviewHistoryDepth);
      return;
    }

    if (fromReviewGuard) {
      this.replaceReviewGuard('offers');
      return;
    }
    this.moveToReviewGuard('offers');
  },

  init() {
    const rootElement = this.getAlpineMagic().$el;
    const reviewElement = rootElement.matches('[data-user-review]')
      ? rootElement
      : rootElement.querySelector<HTMLElement>('[data-user-review]');
    if (!reviewElement) {
      return;
    }

    const dataset = reviewElement.dataset;
    const initialAnswers = JSON.parse(dataset.initialAnswers as string) as Record<string, ReviewAnswer>;

    this.setupReviewFlow(
      Number(dataset.reviewQuestionCount),
      dataset.requiresSafetyCheck === 'true',
      initialAnswers,
      Number(dataset.initialStep),
      dataset.automatic === 'true',
      dataset.bulkNewUrl as string,
      Number(dataset.reviewStartStep)
    );
  }
};
